import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuoteRequest, useSendQuoteResponse, useRequestQuoteVisit, useApproveQuoteVisit, useRejectQuoteVisit, useCompleteQuoteVisit } from '@/hooks/useQuoteRequests'
import Button from '@/components/common/Button/Button'
import Input from '@/components/forms/Input/Input'
import DatePicker from '@/components/forms/DatePicker/DatePicker'
import DateTimePicker from '@/components/forms/DateTimePicker/DateTimePicker'
import Loading from '@/components/common/Loading/Loading'
import { toast } from 'react-hot-toast'
import { FaUser, FaMapMarkerAlt, FaCalendar, FaClock, FaDollarSign, FaFileAlt, FaUsers, FaCheckCircle, FaTimesCircle, FaExclamationCircle, FaTools, FaComments } from 'react-icons/fa'
import './QuoteDetail.css'

interface Participant {
    name: string
    dni: string
    // cul: string // TODO: CUL will be a PDF file upload in the future, currently optional and commented out
}

interface Tool {
    name: string
}

const QuoteDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { data: quote, isLoading } = useQuoteRequest(Number(id))
    const sendQuote = useSendQuoteResponse()
    const requestVisit = useRequestQuoteVisit()
    const approveVisit = useApproveQuoteVisit()
    const rejectVisit = useRejectQuoteVisit()
    const completeVisit = useCompleteQuoteVisit()

    const [showSendQuoteForm, setShowSendQuoteForm] = useState(false)
    const [showRequestVisitForm, setShowRequestVisitForm] = useState(false)
    const [quotedTotal, setQuotedTotal] = useState('')
    const [quotedHours, setQuotedHours] = useState('')
    const [providerNotes, setProviderNotes] = useState('')
    const [serviceStartDate, setServiceStartDate] = useState<Date | null>(null)
    const [serviceStartTime, setServiceStartTime] = useState('')
    const [serviceEndDate, setServiceEndDate] = useState<Date | null>(null)
    const [serviceEndTime, setServiceEndTime] = useState('')
    const [participants, setParticipants] = useState<Participant[]>([])
    const [tools, setTools] = useState<Tool[]>([])
    const [visitDateTime, setVisitDateTime] = useState('')
    const [visitCost, setVisitCost] = useState('')
    const [visitNotes, setVisitNotes] = useState('')

    // Ensure only one form is shown at a time
    useEffect(() => {
        if (showSendQuoteForm) {
            setShowRequestVisitForm(false)
        }
    }, [showSendQuoteForm])

    useEffect(() => {
        if (showRequestVisitForm) {
            setShowSendQuoteForm(false)
        }
    }, [showRequestVisitForm])

    useEffect(() => {
        if (quote) {
            if (quote.quoted_total) setQuotedTotal(quote.quoted_total.toString())
            if (quote.quoted_hours) setQuotedHours(quote.quoted_hours.toString())
            if (quote.provider_notes) setProviderNotes(quote.provider_notes)
            if (quote.service_start_date) {
                const start = new Date(quote.service_start_date)
                setServiceStartDate(start)
                setServiceStartTime(start.toTimeString().slice(0, 5))
            }
            if (quote.service_end_date) {
                const end = new Date(quote.service_end_date)
                setServiceEndDate(end)
                setServiceEndTime(end.toTimeString().slice(0, 5))
            }
            if (quote.quotation_participants) {
                setParticipants(quote.quotation_participants)
            }
            if (quote.quotation_implements) {
                setTools(quote.quotation_implements)
            }
            if (quote.visit_datetime) {
                const visitDate = new Date(quote.visit_datetime)
                setVisitDateTime(visitDate.toISOString().slice(0, 16))
            }
            if (quote.visit_cost) setVisitCost(quote.visit_cost.toString())
            if (quote.visit_notes) setVisitNotes(quote.visit_notes)
        }
    }, [quote])

    const handleAddParticipant = () => {
        setParticipants([...participants, { name: '', dni: '' }])
    }

    const handleRemoveParticipant = (index: number) => {
        setParticipants(participants.filter((_, i) => i !== index))
    }

    const handleParticipantChange = (index: number, field: keyof Participant, value: string) => {
        const updated = [...participants]
        updated[index] = { ...updated[index], [field]: value }
        setParticipants(updated)
    }

    const handleAddTool = () => {
        setTools([...tools, { name: '' }])
    }

    const handleRemoveTool = (index: number) => {
        setTools(tools.filter((_, i) => i !== index))
    }

    const handleToolChange = (index: number, value: string) => {
        const updated = [...tools]
        updated[index] = { name: value }
        setTools(updated)
    }

    const handleSendQuote = async () => {
        if (!quotedTotal || !serviceStartDate || !serviceStartTime || !serviceEndDate || !serviceEndTime) {
            toast.error('Completa todos los campos requeridos')
            return
        }

        const startDateStr = serviceStartDate.toISOString().split('T')[0]
        const endDateStr = serviceEndDate.toISOString().split('T')[0]
        const startDateTime = new Date(`${startDateStr}T${serviceStartTime}`)
        const endDateTime = new Date(`${endDateStr}T${serviceEndTime}`)

        if (endDateTime <= startDateTime) {
            toast.error('La fecha de fin debe ser posterior a la fecha de inicio')
            return
        }

        try {
            await sendQuote.mutateAsync({
                id: Number(id),
                payload: {
                    quoted_total: parseFloat(quotedTotal),
                    quoted_hours: quotedHours ? parseInt(quotedHours) : null,
                    provider_notes: providerNotes,
                    service_start_date: startDateTime.toISOString(),
                    service_end_date: endDateTime.toISOString(),
                    participants: participants.filter(p => p.name && p.dni).map(p => ({ name: p.name, dni: p.dni })), // Only send name and DNI, CUL will be PDF in future
                    implements: tools.filter(t => t.name).map(t => ({ name: t.name })), // Only send tools with names
                }
            })
            toast.success('Cotización enviada exitosamente')
            setShowSendQuoteForm(false)
            setShowRequestVisitForm(false) // Ensure visit form is hidden
            setQuotedTotal('')
            setQuotedHours('')
            setProviderNotes('')
            setServiceStartDate(null)
            setServiceStartTime('')
            setServiceEndDate(null)
            setServiceEndTime('')
            setParticipants([])
            setTools([])
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al enviar cotización')
        }
    }

    const handleRequestVisit = async () => {
        if (!visitDateTime || !visitCost) {
            toast.error('Completa todos los campos requeridos')
            return
        }

        try {
            await requestVisit.mutateAsync({
                id: Number(id),
                payload: {
                    visit_datetime: new Date(visitDateTime).toISOString(),
                    visit_cost: parseFloat(visitCost),
                    visit_notes: visitNotes,
                }
            })
            toast.success('Visita solicitada exitosamente')
            setShowRequestVisitForm(false)
            setShowSendQuoteForm(false) // Ensure quote form is hidden
            setVisitDateTime('')
            setVisitCost('')
            setVisitNotes('')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al solicitar visita')
        }
    }

    const handleApproveVisit = async () => {
        try {
            await approveVisit.mutateAsync({ id: Number(id), payload: {} })
            toast.success('Visita aprobada')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al aprobar visita')
        }
    }

    const handleRejectVisit = async () => {
        try {
            await rejectVisit.mutateAsync({ id: Number(id), payload: {} })
            toast.success('Visita rechazada')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al rechazar visita')
        }
    }

    const handleCompleteVisit = async () => {
        try {
            await completeVisit.mutateAsync({ id: Number(id), payload: {} })
            toast.success('Visita marcada como completada')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al completar visita')
        }
    }

    const getStatusColor = (status: string) => {
        const statusMap: Record<string, string> = {
            pending: '#F59E0B',
            sent: '#3B82F6',
            accepted: '#10B981',
            declined: '#EF4444',
            expired: '#6B7280',
            cancelled: '#EF4444',
        }
        return statusMap[status] || '#6B7280'
    }

    const getVisitStatusColor = (status: string) => {
        const statusMap: Record<string, string> = {
            none: '#6B7280',
            requested: '#F59E0B',
            approved: '#3B82F6',
            completed: '#10B981',
            rejected: '#EF4444',
        }
        return statusMap[status] || '#6B7280'
    }

    if (isLoading) return <Loading />

    if (!quote) {
        return (
            <div className="quote-detail-page-wrapper">
                <div className="quote-detail-container">
                    <div className="quote-detail-error">Cotización no encontrada</div>
                </div>
            </div>
        )
    }

    return (
        <div className="quote-detail-page-wrapper">
            <div className="quote-detail-container">
                <div className="quote-detail-header">
                    <Button variant="secondary" size="md" onClick={() => navigate(-1)}>
                        ← Volver
                    </Button>
                    <div className="quote-detail-header-info">
                        <h1>Cotización #{quote.id}</h1>
                        <div className="quote-detail-status-badge" style={{ backgroundColor: `${getStatusColor(quote.status)}20`, color: getStatusColor(quote.status) }}>
                            {quote.status_display}
                        </div>
                    </div>
                    {quote.has_conversation && quote.conversation_id && (
                        <Button 
                            variant="primary" 
                            size="md"
                            onClick={() => navigate(`/chat/${quote.conversation_id}`)}
                        >
                            <FaComments /> Chat
                        </Button>
                    )}
                </div>

                <div className="quote-detail-grid">
                    {/* Service Information Card */}
                    <div className="quote-detail-card quote-detail-main-card">
                        <h2 className="quote-detail-card-title">Información del Servicio</h2>
                        <div className="quote-detail-info-grid">
                            <div className="quote-detail-info-item">
                                <FaFileAlt className="quote-detail-info-icon" />
                                <div>
                                    <div className="quote-detail-info-label">Servicio</div>
                                    <div className="quote-detail-info-value">{quote.service_name}</div>
                                </div>
                            </div>
                            <div className="quote-detail-info-item">
                                <FaUser className="quote-detail-info-icon" />
                                <div>
                                    <div className="quote-detail-info-label">Cliente</div>
                                    {/* TODO PUT THE USER'S NAME HERE */}
                                    <div className="quote-detail-info-value">Usuario Nuevo</div>
                                </div>
                            </div>
                            <div className="quote-detail-info-item">
                                <FaMapMarkerAlt className="quote-detail-info-icon" />
                                <div>
                                    <div className="quote-detail-info-label">Distrito</div>
                                    <div className="quote-detail-info-value">{quote.district_details?.name || 'No especificado'}</div>
                                </div>
                            </div>
                            {quote.requires_quote_visit && (
                                <div className="quote-detail-info-item">
                                    <FaExclamationCircle className="quote-detail-info-icon" />
                                    <div>
                                        <div className="quote-detail-info-label">Tipo</div>
                                        <div className="quote-detail-info-value">Requiere visita previa</div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {quote.description && (
                            <div className="quote-detail-description">
                                <div className="quote-detail-description-label">Descripción del cliente:</div>
                                <p>{quote.description}</p>
                            </div>
                        )}
                    </div>

                    {/* Visit Status Card */}
                    {quote.visit_status !== 'none' && (
                        <div className="quote-detail-card">
                            <div className="quote-detail-card-header">
                                <h2 className="quote-detail-card-title">Estado de la Visita</h2>
                                <div className="quote-detail-status-badge" style={{ backgroundColor: `${getVisitStatusColor(quote.visit_status)}20`, color: getVisitStatusColor(quote.visit_status) }}>
                                    {quote.visit_status_display}
                                </div>
                            </div>
                            {quote.visit_datetime && (
                                <div className="quote-detail-info-item">
                                    <FaCalendar className="quote-detail-info-icon" />
                                    <div>
                                        <div className="quote-detail-info-label">Fecha y Hora</div>
                                        <div className="quote-detail-info-value">{new Date(quote.visit_datetime).toLocaleString('es-ES')}</div>
                                    </div>
                                </div>
                            )}
                            {quote.visit_cost && (
                                <div className="quote-detail-info-item">
                                    <FaDollarSign className="quote-detail-info-icon" />
                                    <div>
                                        <div className="quote-detail-info-label">Costo</div>
                                        <div className="quote-detail-info-value">S/ {quote.visit_cost}</div>
                                    </div>
                                </div>
                            )}
                            {quote.visit_notes && (
                                <div className="quote-detail-description">
                                    <div className="quote-detail-description-label">Notas:</div>
                                    <p>{quote.visit_notes}</p>
                                </div>
                            )}
                            {quote.visit_status === 'requested' && quote.visit_requested_by === 'provider' && !quote.visit_datetime && (
                                <div className="quote-detail-actions">
                                    <Button variant="primary" size="lg" onClick={() => setShowRequestVisitForm(true)}>
                                        Configurar Detalles de la Visita
                                    </Button>
                                </div>
                            )}
                            {quote.visit_status === 'requested' && quote.visit_requested_by === 'receiver' && (
                                <div className="quote-detail-actions">
                                    <Button variant="success" size="lg" onClick={handleApproveVisit}>
                                        <FaCheckCircle /> Aprobar Visita
                                    </Button>
                                    <Button variant="danger" size="lg" onClick={handleRejectVisit}>
                                        <FaTimesCircle /> Rechazar Visita
                                    </Button>
                                </div>
                            )}
                            {quote.visit_status === 'approved' && quote.can_provider_complete_visit && (
                                <div className="quote-detail-actions">
                                    <Button variant="primary" size="lg" onClick={handleCompleteVisit}>
                                        <FaCheckCircle /> Marcar Visita como Completada
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sent Quote Card */}
                    {quote.status === 'sent' && (
                        <div className="quote-detail-card">
                            <h2 className="quote-detail-card-title">Cotización Enviada</h2>
                            <div className="quote-detail-quote-summary">
                                <div className="quote-detail-quote-amount">
                                    <div className="quote-detail-quote-label">Total Cotizado</div>
                                    <div className="quote-detail-quote-value">S/ {quote.quoted_total}</div>
                                </div>
                                {quote.quoted_hours && (
                                    <div className="quote-detail-info-item">
                                        <FaClock className="quote-detail-info-icon" />
                                        <div>
                                            <div className="quote-detail-info-label">Horas estimadas</div>
                                            <div className="quote-detail-info-value">{quote.quoted_hours} horas</div>
                                        </div>
                                    </div>
                                )}
                                {quote.service_start_date && (
                                    <div className="quote-detail-info-item">
                                        <FaCalendar className="quote-detail-info-icon" />
                                        <div>
                                            <div className="quote-detail-info-label">Fecha de inicio</div>
                                            <div className="quote-detail-info-value">{new Date(quote.service_start_date).toLocaleString('es-ES')}</div>
                                        </div>
                                    </div>
                                )}
                                {quote.service_end_date && (
                                    <div className="quote-detail-info-item">
                                        <FaCalendar className="quote-detail-info-icon" />
                                        <div>
                                            <div className="quote-detail-info-label">Fecha de fin</div>
                                            <div className="quote-detail-info-value">{new Date(quote.service_end_date).toLocaleString('es-ES')}</div>
                                        </div>
                                    </div>
                                )}
                                {quote.provider_notes && (
                                    <div className="quote-detail-description">
                                        <div className="quote-detail-description-label">Notas para el cliente:</div>
                                        <p>{quote.provider_notes}</p>
                                    </div>
                                )}
                                {quote.quotation_participants && quote.quotation_participants.length > 0 && (
                                    <div className="quote-detail-participants-section">
                                        <div className="quote-detail-description-label">
                                            <FaUsers /> Participantes ({quote.quotation_participants.length})
                                        </div>
                                        <div className="quote-detail-participants-list">
                                            {quote.quotation_participants.map((p: any, idx: number) => (
                                                <div key={idx} className="quote-detail-participant-item">
                                                    <strong>{p.name}</strong>
                                                    <span>DNI: {p.dni}</span>
                                                    {/* TODO: CUL will be a PDF file upload in the future
                                                    {p.cul && <span>CUL: {p.cul}</span>}
                                                    */}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {quote.quotation_implements && quote.quotation_implements.length > 0 && (
                                    <div className="quote-detail-participants-section">
                                        <div className="quote-detail-description-label">
                                            <FaTools /> Herramientas/Implementos ({quote.quotation_implements.length})
                                        </div>
                                        <div className="quote-detail-participants-list">
                                            {quote.quotation_implements.map((i: any, idx: number) => (
                                                <div key={idx} className="quote-detail-participant-item">
                                                    <strong>{i.name}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Action Cards */}
                    {/* If visit is completed, show quote option (only if quote hasn't been sent) */}
                    {quote.visit_status === 'completed' && quote.status !== 'sent' && quote.can_provider_send_quote && !showSendQuoteForm && (
                        <div className="quote-detail-card quote-detail-action-card">
                            <h2 className="quote-detail-card-title">Enviar Cotización</h2>
                            <p className="quote-detail-action-description">
                                La visita ha sido completada. Ahora puedes enviar la cotización al cliente.
                            </p>
                            <Button variant="primary" size="lg" onClick={() => setShowSendQuoteForm(true)}>
                                Crear Cotización
                            </Button>
                        </div>
                    )}

                    {/* If no visit or visit rejected, show both options (provider chooses one) */}
                    {/* Only show action cards when no form is active and quote hasn't been sent */}
                    {(quote.visit_status === 'none' || quote.visit_status === 'rejected') && quote.status !== 'sent' && !showSendQuoteForm && !showRequestVisitForm && (
                        <>
                            {/* Quote option - can send directly without visit */}
                            {quote.can_provider_send_quote && (
                                <div className="quote-detail-card quote-detail-action-card">
                                    <h2 className="quote-detail-card-title">Enviar Cotización</h2>
                                    <p className="quote-detail-action-description">
                                        Completa el formulario con los detalles de la cotización para enviarla al cliente.
                                    </p>
                                    <Button variant="primary" size="lg" onClick={() => {
                                        setShowSendQuoteForm(true)
                                        setShowRequestVisitForm(false) // Ensure visit form is hidden
                                    }}>
                                        Crear Cotización
                                    </Button>
                                </div>
                            )}

                            {/* Visit option - optional, can request visit if needed */}
                            {quote.can_provider_request_visit && (
                                <div className="quote-detail-card quote-detail-action-card">
                                    <h2 className="quote-detail-card-title">Solicitar Visita (Opcional)</h2>
                                    <p className="quote-detail-action-description">
                                        Si necesitas evaluar el trabajo antes de cotizar, puedes solicitar una visita previa.
                                    </p>
                                    <Button variant="secondary" size="lg" onClick={() => {
                                        setShowRequestVisitForm(true)
                                        setShowSendQuoteForm(false) // Ensure quote form is hidden
                                    }}>
                                        Solicitar Visita
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {/* If visit is in progress (requested/approved), only show visit actions */}
                    {(quote.visit_status === 'requested' || quote.visit_status === 'approved') && quote.can_provider_request_visit && !showRequestVisitForm && (
                        <div className="quote-detail-card quote-detail-action-card">
                            <h2 className="quote-detail-card-title">Configurar Visita</h2>
                            <p className="quote-detail-action-description">
                                {quote.visit_status === 'requested' && quote.visit_requested_by === 'provider' && !quote.visit_datetime
                                    ? 'Configura los detalles de la visita (fecha, costo y notas).'
                                    : 'Debes completar la visita antes de poder enviar la cotización.'}
                            </p>
                            {quote.visit_status === 'requested' && quote.visit_requested_by === 'provider' && !quote.visit_datetime && (
                                <Button variant="primary" size="lg" onClick={() => setShowRequestVisitForm(true)}>
                                    Configurar Detalles
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Send Quote Form */}
                    {showSendQuoteForm && (
                        <div className="quote-detail-card quote-detail-form-card">
                            <h2 className="quote-detail-card-title">Formulario de Cotización</h2>
                            <div className="quote-detail-form">
                                <div className="quote-detail-form-row">
                                    <Input
                                        name="quotedTotal"
                                        label="Total (S/)"
                                        type="number"
                                        value={quotedTotal}
                                        setValue={setQuotedTotal}
                                        isRequired
                                        placeholder="0.00"
                                        effect3d
                                    />
                                    <Input
                                        name="quotedHours"
                                        label="Horas estimadas (opcional)"
                                        type="number"
                                        value={quotedHours}
                                        setValue={setQuotedHours}
                                        placeholder="Ej: 8"
                                        effect3d
                                    />
                                </div>
                                <div className="quote-detail-form-row">
                                    <DatePicker
                                        label="Fecha de inicio"
                                        value={serviceStartDate}
                                        onChange={setServiceStartDate}
                                        required
                                        effect3d
                                    />
                                    <Input
                                        name="serviceStartTime"
                                        label="Hora de inicio"
                                        type="text"
                                        value={serviceStartTime}
                                        setValue={setServiceStartTime}
                                        placeholder="HH:MM"
                                        isRequired
                                        effect3d
                                    />
                                </div>
                                <div className="quote-detail-form-row">
                                    <DatePicker
                                        label="Fecha de fin"
                                        value={serviceEndDate}
                                        onChange={setServiceEndDate}
                                        required
                                        effect3d
                                    />
                                    <Input
                                        name="serviceEndTime"
                                        label="Hora de fin"
                                        type="text"
                                        value={serviceEndTime}
                                        setValue={setServiceEndTime}
                                        placeholder="HH:MM"
                                        isRequired
                                        effect3d
                                    />
                                </div>
                                <Input
                                    name="providerNotes"
                                    label="Descripción para el cliente"
                                    value={providerNotes}
                                    setValue={setProviderNotes}
                                    multiline
                                    rows={4}
                                    placeholder="Describe los detalles del servicio, materiales, condiciones, etc."
                                    effect3d
                                />
                                <div className="quote-detail-participants-section">
                                    <div className="quote-detail-participants-header">
                                        <label>Participantes (opcional)</label>
                                        <Button variant="ghost" size="sm" onClick={handleAddParticipant}>
                                            <FaUsers /> Agregar Participante
                                        </Button>
                                    </div>
                                    {participants.length > 0 && (
                                        <div className="quote-detail-participants-form">
                                            {participants.map((p, idx) => (
                                                <div key={idx} className="quote-detail-participant-form-item">
                                                    <Input
                                                        name={`participant-name-${idx}`}
                                                        placeholder="Nombre completo"
                                                        value={p.name}
                                                        setValue={(value) => handleParticipantChange(idx, 'name', value)}
                                                        effect3d
                                                    />
                                                    <Input
                                                        name={`participant-dni-${idx}`}
                                                        placeholder="DNI"
                                                        value={p.dni}
                                                        setValue={(value) => handleParticipantChange(idx, 'dni', value)}
                                                        effect3d
                                                    />
                                                    {/* TODO: CUL will be a PDF file upload in the future
                                                    <Input
                                                        name={`participant-cul-${idx}`}
                                                        placeholder="CUL (opcional)"
                                                        value={p.cul}
                                                        setValue={(value) => handleParticipantChange(idx, 'cul', value)}
                                                        effect3d
                                                    />
                                                    */}
                                                    <Button variant="ghost" size="sm" onClick={() => handleRemoveParticipant(idx)}>
                                                        <FaTimesCircle />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="quote-detail-participants-section">
                                    <div className="quote-detail-participants-header">
                                        <label>Herramientas/Implementos (opcional)</label>
                                        <Button variant="ghost" size="sm" onClick={handleAddTool}>
                                            <FaTools /> Agregar Herramienta
                                        </Button>
                                    </div>
                                    {tools.length > 0 && (
                                        <div className="quote-detail-participants-form">
                                            {tools.map((tool, idx) => (
                                                <div key={idx} className="quote-detail-participant-form-item">
                                                    <Input
                                                        name={`tool-name-${idx}`}
                                                        placeholder="Nombre de la herramienta/implemento"
                                                        value={tool.name}
                                                        setValue={(value) => handleToolChange(idx, value)}
                                                        effect3d
                                                    />
                                                    <Button variant="ghost" size="sm" onClick={() => handleRemoveTool(idx)}>
                                                        <FaTimesCircle />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="quote-detail-actions">
                                    <Button variant="primary" size="lg" onClick={handleSendQuote} disabled={sendQuote.isPending}>
                                        {sendQuote.isPending ? 'Enviando...' : 'Enviar Cotización'}
                                    </Button>
                                    <Button variant="secondary" size="lg" onClick={() => setShowSendQuoteForm(false)}>
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Request Visit Form */}
                    {showRequestVisitForm && (
                        <div className="quote-detail-card quote-detail-form-card">
                            <h2 className="quote-detail-card-title">
                                {quote.visit_status === 'requested' && quote.visit_requested_by === 'provider' && !quote.visit_datetime
                                    ? 'Configurar Detalles de la Visita'
                                    : 'Solicitar Visita'}
                            </h2>
                            <div className="quote-detail-form">
                                <DateTimePicker
                                    name="visitDateTime"
                                    label="Fecha y hora de la visita"
                                    value={visitDateTime}
                                    setValue={setVisitDateTime}
                                    isRequired
                                    effect3d
                                />
                                <Input
                                    name="visitCost"
                                    label="Costo de la visita (S/)"
                                    type="number"
                                    value={visitCost}
                                    setValue={setVisitCost}
                                    isRequired
                                    placeholder="0.00"
                                    effect3d
                                />
                                <Input
                                    name="visitNotes"
                                    label="Notas sobre la visita"
                                    value={visitNotes}
                                    setValue={setVisitNotes}
                                    multiline
                                    rows={4}
                                    placeholder="Describe qué se evaluará en la visita, qué debe preparar el cliente, etc."
                                    effect3d
                                />
                                <div className="quote-detail-actions">
                                    <Button variant="primary" size="lg" onClick={handleRequestVisit} disabled={requestVisit.isPending}>
                                        {requestVisit.isPending 
                                            ? 'Guardando...' 
                                            : (quote.visit_status === 'requested' && quote.visit_requested_by === 'provider' && !quote.visit_datetime
                                                ? 'Guardar Detalles'
                                                : 'Solicitar Visita')}
                                    </Button>
                                    <Button variant="secondary" size="lg" onClick={() => setShowRequestVisitForm(false)}>
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default QuoteDetail
